import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CartItem } from '../components/restaurant/CartItem';
import { useCart } from '../lib/cart';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useLanguage } from '../lib/i18n';
import { ShoppingBag, ArrowLeft, Store } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    updateQuantity,
    removeItem,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    t
  } = useLanguage();
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (cart.restaurantId) {
      console.log('[NOIZA][CartPage] Registering restaurant:', cart.restaurantId);
      monitorRestaurant(cart.restaurantId);
    }
  }, [cart.restaurantId, monitorRestaurant]);
  const subtotal = getTotal();
  const discount = getDiscount();
  // In a real app, delivery fee would be calculated based on distance/settings
  const deliveryFeeEstimate = cart.items.length > 0 ? 1500 : 0;
  const total = subtotal + deliveryFeeEstimate;
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-3973">
        <MarketplaceHeader data-id="element-3974" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-3975">
          <EmptyState icon={ShoppingBag} title={t('cart_empty')} description={t('cart_empty_desc')} actionLabel={t('explore')} onAction={() => navigate('/restaurants')} data-id="element-3976" />
        </main>
        <Footer data-id="element-3977" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-3978">
      <MarketplaceHeader data-id="element-3979" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8" data-id="element-3980">
        <div className="flex items-center justify-between mb-8" data-id="element-3981">
          <div className="flex items-center gap-4" data-id="element-3982">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-3983">
              <ArrowLeft className="w-6 h-6" data-id="element-3984" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-3985">
              {t('cart_title')}
            </h1>
          </div>
          <button onClick={clearCart} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors" data-id="element-3986">
            {t('cart_clear')}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-id="element-3987">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-3988">
            <Store className="w-5 h-5 text-gray-500" data-id="element-3989" />
            <span className="font-semibold text-gray-900" data-id="element-3990">
              {cart.restaurantName}
            </span>
          </div>

          <div className="px-6 py-2" data-id="element-3991">
            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-3992" />)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-3993">
          <div className="space-y-4" data-id="element-3994">
            <div className="flex justify-between text-gray-600" data-id="element-3995">
              <span data-id="element-3996">{t('subtotal')}</span>
              <span data-id="element-3997">{formatCurrency(subtotal + (discount?.amount ?? 0))}</span>
            </div>
            {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-3998">
                <span data-id="element-3999">{discount.label}</span>
                <span data-id="element-4000">-{formatCurrency(discount.amount)}</span>
              </div>}
            <div className="flex justify-between text-gray-600" data-id="element-4001">
              <span data-id="element-4002">{t('delivery_fee')}</span>
              <span data-id="element-4003">{formatCurrency(deliveryFeeEstimate)}</span>
            </div>
            <div className="pt-4 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-4004">
              <span data-id="element-4005">{t('total')}</span>
              <span data-id="element-4006">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <Button onClick={() => navigate('/checkout')} className="w-full h-14 text-lg" disabled={isKitchenPaused} data-id="element-4007">
          {isKitchenPaused ? t('kitchen_paused') : t('cart_checkout')}
        </Button>
      </main>

      <Footer data-id="element-4008" />
    </div>;
}